import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import styled from '@emotion/styled';
import { LoadingButton } from '@mui/lab';
import {
  DialogTitle,
  FormControl,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import rtlPlugin from '@mui/stylis-plugin-rtl';
import { AdapterDateFnsJalali } from '@mui/x-date-pickers/AdapterDateFnsJalaliV3';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { faIR as pickersFaIR } from '@mui/x-date-pickers/locales';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import VersionInput from 'components/private/VersionControl/VersionInput';
import FileUploader from 'components/shared/fileUploader/FileUploader';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { prefixer } from 'stylis';

const defaultValues = {
  version: ['', '', '', ''],
  dateTime: null,
  isForced: false,
  logs: '',
  file: { pondFiles: [], fileKey: null },
};

const parseVersionFromTitle = title => {
  const parts = title?.split('.') ?? [];

  return [...Array(4)].map((_, index) => parts[index] ?? '');
};

const buildFormValues = initialValues => {
  if (!initialValues) {
    return defaultValues;
  }

  const version =
    initialValues.version?.length === 4
      ? initialValues.version
      : parseVersionFromTitle(initialValues.name);

  return {
    version,
    dateTime: initialValues.dateTime ? new Date(initialValues.dateTime) : null,
    isForced: initialValues.isForced ?? false,
    logs: initialValues.logs ?? '',
    file: { pondFiles: [], fileKey: initialValues.fileKey ?? null },
  };
};

const VersionControlEditModal = ({
  open,
  handleClose,
  applicationGuid,
  versionGuid,
  initialValues,
  editLoading,
  onEdit,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm({ defaultValues });

  useEffect(() => {
    if (open) {
      reset(buildFormValues(initialValues));
    } else {
      reset(defaultValues);
    }
  }, [open, initialValues, reset]);

  const onSubmit = data => {
    onEdit({
      guid: versionGuid,
      applicationGuid,
      version: data.version,
      dateTime: data.dateTime ? data.dateTime.toISOString() : null,
      isForced: data.isForced,
      fileKey: data.file.fileKey,
      logs: data.logs,
    });
  };

  const cacheRtl = createCache({
    key: 'adapter-date-fns-jalali-demo',
    stylisPlugins: [prefixer, rtlPlugin],
  });

  const existingTheme = useTheme();

  return (
    <Dialog open={open} onClose={handleClose} sx={{ direction: 'rtl' }}>
      <DialogTitle>ویرایش نسخه</DialogTitle>

      <DialogContent>
        <FormControl variant="standard" sx={{ m: 2, minWidth: 300 }}>
          <Typography variant="caption" sx={{ mb: 1, color: 'text.secondary' }}>
            نسخه
          </Typography>

          <Controller
            name="version"
            control={control}
            rules={{
              validate: value =>
                value.every(v => v !== '') || 'تمام بخش‌های نسخه الزامی است',
            }}
            render={({ field }) => (
              <VersionInput
                value={field.value}
                onChange={field.onChange}
                error={isSubmitted ? !!errors.version : null}
              />
            )}
          />
        </FormControl>

        <FieldWrapper>
          <CacheProvider value={cacheRtl}>
            <ThemeProvider theme={existingTheme}>
              <div dir="rtl">
                <LocalizationProvider
                  dateAdapter={AdapterDateFnsJalali}
                  localeText={{
                    ...pickersFaIR.components.MuiLocalizationProvider
                      .defaultProps.localeText,
                    okButtonLabel: 'تأیید',
                  }}
                >
                  <Controller
                    name="dateTime"
                    control={control}
                    // rules={{ required: 'تاریخ انتشار الزامی است' }}
                    render={({ field }) => (
                      <DateTimePicker
                        label="تاریخ انتشار"
                        value={field.value}
                        onChange={field.onChange}
                        slotProps={{
                          desktopPaper: { dir: 'rtl' },
                          mobilePaper: { dir: 'rtl' },
                          textField: {
                            variant: 'standard',
                            fullWidth: true,
                            error: isSubmitted && !!errors.dateTime,
                            helperText: isSubmitted
                              ? errors.dateTime?.message
                              : undefined,
                            InputLabelProps: {
                              sx: {
                                transformOrigin: 'left',
                                right: 'inherit',
                                left: '1.75rem',
                              },
                            },
                          },
                        }}
                        sx={{ width: '100%' }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </div>
            </ThemeProvider>
          </CacheProvider>
        </FieldWrapper>

        <FieldWrapper>
          <Controller
            name="isForced"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch checked={field.value} onChange={field.onChange} />
                }
                label="به‌روزرسانی اجباری"
                labelPlacement="start"
                sx={{ justifyContent: 'space-between', width: '100%', mr: 0 }}
              />
            )}
          />
        </FieldWrapper>

        <FieldWrapper>
          <Typography variant="caption" sx={{ mb: 1, color: 'text.secondary' }}>
            فایل نصبی
          </Typography>
          <UploadArea>
            <Controller
              name="file"
              control={control}
              rules={{
                validate: value =>
                  !!value?.fileKey || 'بارگذاری فایل الزامی است',
              }}
              render={({ field }) => (
                <FileUploader
                  files={field.value?.pondFiles ?? []}
                  labelIdle="فایل را اینجا رها کنید"
                  allowMultiple={false}
                  maxFiles={1}
                  onprocessfile={(error, pondFile) => {
                    if (!error && pondFile.serverId) {
                      const currentFile = getValues('file');
                      field.onChange({
                        ...currentFile,
                        fileKey: pondFile.serverId,
                      });
                    }
                  }}
                  onupdatefiles={fileItems => {
                    const currentFile = getValues('file');
                    field.onChange({
                      pondFiles: fileItems.map(item => item.file),
                      fileKey:
                        fileItems[0]?.serverId ?? currentFile?.fileKey ?? null,
                    });
                  }}
                />
              )}
            />
            {isSubmitted && errors.file ? (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: 'block' }}
              >
                {errors.file.message}
              </Typography>
            ) : null}
          </UploadArea>
        </FieldWrapper>

        <FieldWrapper>
          <Controller
            name="logs"
            control={control}
            render={({ field }) => (
              <TextField
                label="توضیحات"
                multiline
                rows={4}
                fullWidth
                variant="standard"
                {...field}
              />
            )}
          />
        </FieldWrapper>
      </DialogContent>

      <DialogActions sx={{ gap: 1, padding: 3 }}>
        <Button
          onClick={handleClose}
          variant="contained"
          disabled={editLoading}
        >
          انصراف
        </Button>
        <LoadingButton
          color="warning"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          loading={editLoading}
        >
          ویرایش
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

const FieldWrapper = styled.div`
  margin: 20px 8px 0;
  min-width: 300px;
`;

const UploadArea = styled.div`
  margin: 20px 0;
  display: block;
  justify-content: center;
  padding: 20px;
  width: 100%;
  border-radius: 16px;
  background-color: #edf0f4;
  & .filepond--credits {
    display: none !important;
  }
`;

export default VersionControlEditModal;
